/**
 * @fileoverview Custom hook for debouncing values with deep comparison.
 */

import {useMemo, useState} from "react";
import {useDeepCompareEffect} from "react-use";
import {debounce} from "lodash";

/**
 * Custom hook that debounces a value using deep comparison.
 * The debounced value will only update after the specified timeout period
 * has passed without the input value changing.
 *
 * @template T The type of the value being debounced.
 * @param {T} value The value to debounce.
 * @param {number} [timeout=1000] The debounce timeout in milliseconds.
 * @returns {T} The debounced value.
 */
export const useDebouncedValue = <T>(value: T, timeout = 1000) => {
  const [debounced, setDebounced] = useState(value);

  const debounceValue = useMemo(
    () => debounce(value => setDebounced(value), timeout),
    [setDebounced, timeout],
  );

  useDeepCompareEffect(() => {
    debounceValue(value);
  }, [value]);

  return debounced;
};
